import { useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import {
  EmployeeSelectorV2MultiSelectState,
  StateManual,
} from 'modules/employees/components/EmployeeSelectorV2/types'
import { BaseEntitySelectorWrapper } from 'modules/finance/components/FinancialWorkspace/components/Filters/BaseEntitySelectorWrapper'

interface Props {
  defaultOpen?: boolean // Not supported by EmployeeSelectorV2
  defaultEmployees?: Array<{ id: string }>
  onChange: (value: string[] | undefined) => void
}

const EmployeeSelector = ({ onChange, defaultEmployees }: Props) => {
  const [employeeSelection, setEmployeeSelection] = useState<StateManual>({
    type: 'manual',
    ids: [],
  })
  const { canSeeAllCompanyData } = useOutletContext<{
    canSeeAllCompanyData: boolean
  }>()

  const handleValueChanged = useUnsafeCallback(
    (value: EmployeeSelectorV2MultiSelectState) => {
      // With disableSelectAll, state will always be 'manual'
      if (value.type === 'manual') {
        setEmployeeSelection(value)
        onChange(value.ids.length > 0 ? value.ids : undefined)
      }
    },
    [onChange]
  )

  // set default value as current value if there is no current value
  useUnsafeEffect(() => {
    if (defaultEmployees?.length && !employeeSelection.ids.length) {
      const defaultIds = defaultEmployees.map((e) => e.id)
      const defaultState: StateManual = {
        type: 'manual',
        ids: defaultIds,
      }
      handleValueChanged(defaultState)
    }
  }, [employeeSelection, defaultEmployees, handleValueChanged])

  const hasEmployeesSelected = employeeSelection.ids.length > 0

  return (
    <EmployeeSelectorV2
      multiple
      onChange={handleValueChanged}
      options={{
        displayNameStrategy: 'full',
        disableSelectAll: true,
      }}
      forcedFilters={{
        onlyManagers: true,
        ...(!canSeeAllCompanyData && { onlyMyReportees: true }),
      }}
    >
      <BaseEntitySelectorWrapper
        selected={hasEmployeesSelected}
        placeholder={i18n.t('common.person.other')}
        selectedTitle={i18n.t('common.person.other')}
        selectedCount={employeeSelection.ids.length}
      />
    </EmployeeSelectorV2>
  )
}

export default EmployeeSelector
